import { useContext, useState, useRef, useEffect } from "react"
import axios from "axios"
import { Calendar } from "primereact/calendar"
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { Button } from "primereact/button"
import { Toast } from "primereact/toast"
import EditorField from "../Utils/EditorField"
import RadioButtonField from "../Utils/RadioButtonField"
import styles from "../../../styles/Participant-Employment.module.css"
import { UserContext } from "../../../context/userContext"
import {
  Actions,
  showError,
  convertToLocalDateTime,
  renderTableDataCell,
} from "../Utils/Utils"
import { YesNo, YesNoUnknown, JobEndingReasons } from "../Types/Dropdowns"
import { employmentApiUrl } from "../../../config/microservicesUrls"

const api_url = employmentApiUrl()

export default function EndJobForm({
  closeForm,
  jobRecord,
  action,
  updateData,
}) {
  const toast = useRef(null)

  const userCtx = useContext(UserContext)

  const [loading, setLoading] = useState(false)

  const [formErrors, setFormErrors] = useState(null)

  const isModal = closeForm !== undefined

  const defaultData = {
    endDate: undefined,
    didSomethingChangedSinceCompleted: undefined,
    jobEndingReason: undefined,
    perspectiveRegardingJobEnding: "",
    staffCommentsAboutJobEnding: "",
    employerCommentsAboutJobEnding: "",
    supportProvidedToParticipant: "",
    doesParticipantWishesAnotherJob: undefined,
    clientPreferencesRegardingDisclosureOnNextJob: "",
  }

  const [data, setData] = useState(defaultData)

  useEffect(() => {
    let recordEndData = { ...defaultData }
    Object.keys(defaultData).forEach((k) => {
      if (jobRecord[k] !== undefined) {
        recordEndData[k] = jobRecord[k]
      }
    })
    recordEndData["endDate"] =
      recordEndData.endDate != null
        ? convertToLocalDateTime(recordEndData.endDate)
        : null
    recordEndData["jobEndingReason"] = recordEndData.jobEndingReason
      ? JobEndingReasons[recordEndData.jobEndingReason]
      : undefined
    recordEndData["didSomethingChangedSinceCompleted"] =
      recordEndData.didSomethingChangedSinceCompleted
        ? YesNo[recordEndData.didSomethingChangedSinceCompleted]
        : undefined
    recordEndData["doesParticipantWishesAnotherJob"] =
      recordEndData.doesParticipantWishesAnotherJob
        ? YesNoUnknown[recordEndData.doesParticipantWishesAnotherJob]
        : undefined

    setData(recordEndData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobRecord])

  const clearForm = () => {
    setData(defaultData)
    setFormErrors(null)
  }

  const handleSave = async () => {
    setLoading(true)
    const jobEndingReason = data.jobEndingReason
      ? JobEndingReasons.indexOf(data.jobEndingReason)
      : undefined
    const didSomethingChangedSinceCompleted =
      data.didSomethingChangedSinceCompleted
        ? YesNo.indexOf(data.didSomethingChangedSinceCompleted)
        : undefined
    const doesParticipantWishesAnotherJob = data.doesParticipantWishesAnotherJob
      ? YesNoUnknown.indexOf(data.doesParticipantWishesAnotherJob)
      : undefined

    axios
      .put(
        `${api_url}/jobRecord/endJob`,
        {
          ...data,
          id: jobRecord.id,
          didSomethingChangedSinceCompleted,
          jobEndingReason,
          doesParticipantWishesAnotherJob,
          updatedBy: userCtx.session.user.staff_id,
        },
        { headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` } }
      )
      .then(function (response) {
        clearForm()
        updateData(response.data)
        setLoading(false)
        closeForm()
      })
      .catch(function (response) {
        const errors = response["response"]["data"]["errors"]
        setLoading(false)
        setFormErrors(errors)
        showError(toast, "Not able to end job record", errors)
      })
  }

  const renderFormErrors = () => {
    if (formErrors !== null) {
      return (
        <ul className={styles.errors_list}>
          {formErrors.map((error, index) => {
            return (
              <li key={index}>
                <small className="p-error">{error}</small>
              </li>
            )
          })}
        </ul>
      )
    }
    return null
  }

  const jobRecordDic = [
    { label: "Job title", value: jobRecord.jobTitle, type: "string" },
    { label: "Employer name", value: jobRecord.employerName, type: "string" },
    {
      label: "First day of employment",
      value: jobRecord.firstEmploymentDay,
      type: "date",
    },
  ]

  const jobRecordEmpty = jobRecordDic.every(
    (item) => item.value === null || item.value === ""
  )

  return (
    <>
      {isModal && <Toast ref={toast} position="top-right" />}
      <div className="main-content">
        {isModal && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h1 className={styles.view_title}>End job</h1>
            <Button
              className={styles.rounded_btn}
              icon="pi pi-times"
              type="button"
              disabled={loading}
              onClick={() => {
                clearForm()
                closeForm()
              }}
            />
          </div>
        )}
        <div className={"card flex flex-wrap p-fluid " + styles.wrapper}>
          {isModal && !jobRecordEmpty && (
            <div>
              <h3 className={styles.view_subtitle}>Job record</h3>
              <hr className={styles.line}></hr>
              <table id="job_record_info" className={styles.table}>
                <thead>
                  <tr>
                    {jobRecordDic.map((item, index) => (
                      <th key={index}>{item.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {jobRecordDic.map((item, index) =>
                      renderTableDataCell(item, index)
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {isModal && <br />}
          <div className={styles.field} style={{ width: "237px" }}>
            <label className={styles.label}>Job end date</label>
            <Calendar
              name="Job End Date"
              panelClassName="custom_calendar_panel"
              value={data.endDate}
              onChange={(e) => setData({ ...data, endDate: e.value })}
              dateFormat="mm/dd/yy"
              showIcon
              className={styles.calendar}
              disabled={action === Actions.View}
            />
          </div>

          <div className={styles.field}>
            <RadioButtonField
              label="Did job duties, work schedule, supervision, or other information change since job start form was completed?"
              value={data.didSomethingChangedSinceCompleted}
              setValue={(e) =>
                setData({ ...data, didSomethingChangedSinceCompleted: e })
              }
              short
              disabled={action === Actions.View}
            />
          </div>

          <div className={styles.field} style={{ width: "237px" }}>
            <label className={styles.label}>Reason for job ending?</label>
            <Dropdown
              style={{ height: "32px", alignItems: "center" }}
              value={data.jobEndingReason}
              options={JobEndingReasons}
              onChange={(e) => setData({ ...data, jobEndingReason: e.value })}
              placeholder="None"
              disabled={action === Actions.View}
            />
          </div>

          <EditorField
            value={data.perspectiveRegardingJobEnding}
            onTextChange={(e) =>
              setData({ ...data, perspectiveRegardingJobEnding: e })
            }
            label="What is your perspective regarding this job ending?"
            disabled={action === Actions.View}
          />

          <EditorField
            value={data.staffCommentsAboutJobEnding}
            onTextChange={(e) =>
              setData({ ...data, staffCommentsAboutJobEnding: e })
            }
            label="Staff comments about job ending"
            disabled={action === Actions.View}
          />

          <EditorField
            value={data.employerCommentsAboutJobEnding}
            onTextChange={(e) =>
              setData({ ...data, employerCommentsAboutJobEnding: e })
            }
            label="Employer comments about job ending"
            disabled={action === Actions.View}
          />

          <EditorField
            value={data.supportProvidedToParticipant}
            onTextChange={(e) =>
              setData({ ...data, supportProvidedToParticipant: e })
            }
            label="Type of support provided to participant"
            disabled={action === Actions.View}
          />

          <div className={styles.field}>
            <RadioButtonField
              label="Does the participant wish to look for another job?"
              value={data.doesParticipantWishesAnotherJob}
              setValue={(e) =>
                setData({ ...data, doesParticipantWishesAnotherJob: e })
              }
              disabled={action === Actions.View}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              Client's preferences regarding disclosure on next job
            </label>
            <InputText
              style={{ width: "70%" }}
              value={data.clientPreferencesRegardingDisclosureOnNextJob}
              onChange={(e) =>
                setData({
                  ...data,
                  clientPreferencesRegardingDisclosureOnNextJob: e.target.value,
                })
              }
              className={"p-inputtext-md"}
              disabled={action === Actions.View}
              name="jobTitle"
            />
          </div>
          <br />
          {isModal && (
            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "flex-end",
              }}
            >
              <Button
                className={styles.cancel_button}
                type="button"
                onClick={() => {
                  clearForm()
                  closeForm()
                }}
                disabled={loading}
              >
                {action === Actions.View ? "Back" : "Cancel"}
              </Button>
              {action !== Actions.View && (
                <Button
                  className={styles.button}
                  disabled={loading}
                  type="submit"
                  icon={loading ? "pi pi-spin pi-spinner" : ""}
                  iconPos="right"
                  label="Save changes"
                  onClick={() => handleSave()}
                />
              )}
            </div>
          )}
          {renderFormErrors()}
        </div>
      </div>
    </>
  )
}
